import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  AUTH_ACCESS_TOKEN_COOKIE,
  AUTH_REFRESH_TOKEN_COOKIE,
  AUTH_USER_COOKIE,
  setSessionCookie,
} from "@/lib/cookies"
import {
  completeEmailVerification,
  fetchEmailVerificationDetails,
} from "@/lib/emailVerification"
import { identifyUser, trackSignUp } from "@/lib/analytics"
import type {
  CompleteEmailVerificationResponse,
  EmailVerificationDetails,
} from "@/types/emailVerification"

const MIN_PASSWORD_LENGTH = 8

function mapVerifiedUserToAuthUser(
  user: CompleteEmailVerificationResponse["user"]
) {
  return {
    id: user.userId,
    email: user.email,
    name: user.displayName ?? undefined,
    role: user.role,
    orgId: user.orgId,
    orgName: user.organization?.name,
    termsAcceptedAt: user.termsAcceptedAt ?? null,
  }
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") ?? ""
  const [details, setDetails] = useState<EmailVerificationDetails | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(true)
  const [detailsError, setDetailsError] = useState<string | null>(null)

  const [displayName, setDisplayName] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [matchError, setMatchError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setLoadingDetails(false)
      return
    }

    let cancelled = false
    ;(async () => {
      const result = await fetchEmailVerificationDetails(token)
      if (cancelled) return
      if (result.ok) {
        setDetails(result.details)
        setDisplayName(result.details.displayName ?? "")
      } else {
        setDetailsError(result.error)
      }
      setLoadingDetails(false)
    })()

    return () => {
      cancelled = true
    }
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setMatchError(null)

    if (!token) {
      setError("Verification link is invalid or missing.")
      return
    }

    if (!displayName.trim()) {
      setError("Name is required.")
      return
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }

    if (password !== confirmPassword) {
      setMatchError("Passwords do not match")
      return
    }

    setSubmitting(true)
    const result = await completeEmailVerification({
      token,
      password,
      displayName: displayName.trim(),
    })
    setSubmitting(false)

    if (!result.ok) {
      setError(result.error)
      return
    }

    const mappedUser = mapVerifiedUserToAuthUser(result.data.user)
    setSessionCookie(AUTH_ACCESS_TOKEN_COOKIE, result.data.tokens.accessToken)
    setSessionCookie(AUTH_REFRESH_TOKEN_COOKIE, result.data.tokens.refreshToken)
    setSessionCookie(AUTH_USER_COOKIE, JSON.stringify(mappedUser))
    identifyUser(mappedUser, "register")
    trackSignUp()
    window.location.replace("/chat")
  }

  if (!token) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm space-y-4 rounded-lg border p-6 shadow-sm text-center">
          <h1 className="text-xl font-semibold">Invalid verification link</h1>
          <p className="text-sm text-muted-foreground">
            This link is missing or incomplete. Start registration again to get
            a new link.
          </p>
          <Button asChild className="w-full">
            <Link to="/register">Back to register</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (loadingDetails) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (detailsError || !details) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm space-y-4 rounded-lg border p-6 shadow-sm text-center">
          <h1 className="text-xl font-semibold">Verification unavailable</h1>
          <p className="text-sm text-muted-foreground" role="alert">
            {detailsError ?? "This verification link is invalid or has expired."}
          </p>
          <Button asChild className="w-full">
            <Link to="/register">Back to register</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border p-6 shadow-sm"
      >
        <h1 className="text-center text-xl font-semibold">
          Finish creating your account
        </h1>
        <p className="text-center text-sm text-muted-foreground">
          Set a password to complete your registration.
        </p>
        {(error || matchError) && (
          <p className="text-center text-sm text-destructive" role="alert">
            {error ?? matchError}
          </p>
        )}
        <div className="space-y-2">
          <label htmlFor="verify-email-address" className="text-sm font-medium">
            Email
          </label>
          <Input
            id="verify-email-address"
            type="email"
            value={details.email}
            readOnly
            disabled
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="verify-name" className="text-sm font-medium">
            Name
          </label>
          <Input
            id="verify-name"
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            className="w-full"
          />
        </div>
        {details.organizationName && (
          <div className="space-y-2">
            <label htmlFor="verify-org" className="text-sm font-medium">
              Organisation
            </label>
            <Input
              id="verify-org"
              type="text"
              value={details.organizationName}
              readOnly
              disabled
              className="w-full"
            />
          </div>
        )}
        <div className="space-y-2">
          <label htmlFor="verify-password" className="text-sm font-medium">
            Password
          </label>
          <Input
            id="verify-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="verify-confirm-password" className="text-sm font-medium">
            Confirm password
          </label>
          <Input
            id="verify-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
            className="w-full"
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  )
}
